import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ShoppingCart } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { MdBookmark } from 'react-icons/md';
import MessageBox from '../../components/MessageBox';
import { Skeleton } from '../../components/ui/skeleton';
import rifaService from '../../services/rifa.service';
import casillaService from '../../services/casilla.service';
import { getError } from '../../utils';
import { paths } from '../../routes/paths';
import { trackAnalyticsEvent } from '../../lib/analytics';
import { isSharedBoard, sharedBoardSize, toTicketsPerSquare } from '../../lib/boardPlans';

function BoardTicketsRifaSkeleton() {
  return (
    <div className='min-h-screen bg-white font-sans'>
      {/* Top bar */}
      <div className='flex items-center gap-3 px-4 py-4 lg:px-8 lg:py-5'>
        <Skeleton className='h-9 w-9 shrink-0 rounded-full' />
        <Skeleton className='h-5 w-48' />
      </div>
      <div className='mx-auto max-w-7xl px-4 lg:px-8'>
        <div className='lg:grid lg:grid-cols-5 lg:gap-10'>
          {/* Left col */}
          <div className='lg:col-span-2 space-y-3'>
            <Skeleton className='w-full aspect-video rounded-2xl' />
            <div className='rounded-2xl bg-zinc-100 p-4 space-y-3'>
              <Skeleton className='h-6 w-3/4' />
              <Skeleton className='h-4 w-full' />
            </div>
            <Skeleton className='hidden lg:block h-12 w-full rounded-xl' />
          </div>
          {/* Right col — category accordion rows */}
          <div className='lg:col-span-3 mt-5 lg:mt-0 space-y-2'>
            <Skeleton className='h-5 w-24 mb-3' />
            {[1, 2, 3].map(i => (
              <div key={i} className='rounded-xl bg-zinc-100 px-4 py-3 flex items-center gap-3'>
                <Skeleton className='h-3 w-3 rounded-full shrink-0' />
                <Skeleton className='h-4 flex-1' />
                <Skeleton className='h-4 w-12' />
                <Skeleton className='h-4 w-10' />
                <Skeleton className='h-4 w-4 rounded' />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Merge duplicate category names, keeping max rows/cols */
function mergeBoardCategories(categories) {
  const m = new Map();
  for (const cat of categories || []) {
    const name = String(cat?.nombre ?? '').trim();
    if (!name) continue;
    const filas = Math.max(1, Number(cat.filas) || 1);
    const columnas = Math.max(1, Number(cat.columnas) || 1);
    const prev = m.get(name);
    if (!prev) {
      m.set(name, { ...cat, nombre: name, filas, columnas });
    } else {
      m.set(name, {
        ...prev,
        filas: Math.max(prev.filas, filas),
        columnas: Math.max(prev.columnas, columnas),
      });
    }
  }
  return [...m.values()];
}

/** Free squares on a shared board (plan colors are only shown once a square is taken). */
const SHARED_FREE_COLOR = '#c4c4c8';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, casillas: action.payload, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

export default function BoardTicketsRifa() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();

  const [{ loading, error, casillas }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    casillas: [],
  });

  const [rifaDetails, setRifaDetails] = useState(null);
  const trackedRaffleRef = useRef(null);
  const [selectedCasillas, setSelectedCasillas] = useState([]);
  // Which category row is expanded (only one at a time)
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const [rifaData, casillasData] = await Promise.all([
          rifaService.getRifa(id),
          casillaService.getCasillasByRifa(id),
        ]);
        const rifa = rifaData?.data ?? rifaData;
        setRifaDetails(rifa);
        if (trackedRaffleRef.current !== id) {
          trackedRaffleRef.current = id;
          trackAnalyticsEvent('raffle_view', { rifaId: id, stage: 'browse' });
        }
        dispatch({ type: 'FETCH_SUCCESS', payload: casillasData });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    fetchData();
  }, [id]);

  const toggleCasilla = casilla => {
    if (casilla.estado !== 'Disponible') return;
    setSelectedCasillas(prev => {
      const exists = prev.find(c => c._id === casilla._id);
      return exists ? prev.filter(c => c._id !== casilla._id) : [...prev, casilla];
    });
  };

  const isSelected = casilla => selectedCasillas.some(c => c._id === casilla._id);

  const shared = isSharedBoard(rifaDetails);
  // Shared board: price / numbers depend on the plan chosen per square in the next step
  const totalPrice = shared
    ? 0
    : selectedCasillas.reduce((sum, c) => sum + (Number(c.precioCasilla) || 0), 0);
  const totalTickets = shared
    ? 0
    : selectedCasillas.reduce((sum, c) => sum + toTicketsPerSquare(c.ticketsPorCasilla), 0);

  const handleProceed = () => {
    if (selectedCasillas.length === 0) {
      toast.error(t('board.selectAtLeastOne') || 'Seleccioná al menos una casilla');
      return;
    }
    trackAnalyticsEvent('ticket_selection', {
      rifaId: id,
      ticketCount: totalTickets,
      stage: 'selection',
    });
    navigate(paths.reserveBoard, {
      state: { casillas: selectedCasillas, rifaInfo: rifaDetails, totalPrice, totalTickets },
      replace: true,
    });
  };

  const categories = mergeBoardCategories(rifaDetails?.config?.categories || []);
  const casillasByCategory = {};
  casillas.forEach(c => {
    const key = String(c.categoria ?? '').trim();
    if (!key) return;
    if (!casillasByCategory[key]) casillasByCategory[key] = [];
    casillasByCategory[key].push(c);
  });

  const buildGrid = (catName, filas, columnas) => {
    const cats = casillasByCategory[String(catName).trim()] || [];
    return Array.from({ length: filas }, (_, r) =>
      Array.from(
        { length: columnas },
        (_, col) => cats.find(c => Number(c.fila) === r && Number(c.columna) === col) || null
      )
    );
  };

  const sharedGrid = sharedBoardSize(rifaDetails?.config);

  const imagenTab = rifaDetails?.imagenTableroVendido;
  const soldSquareBgUrl =
    typeof imagenTab === 'object' && imagenTab?.fileLink ? String(imagenTab.fileLink).trim() : '';

  const getSquareStyle = (casilla, cat) => {
    const sold = casilla.estado === 'Vendido';
    const reserved = casilla.estado === 'Reservado';
    const selected = isSelected(casilla);
    // Shared board: taken squares show the color of the plan the buyer chose
    const planColor = shared && (sold || reserved) ? casilla.color || '' : '';
    if ((sold || reserved) && soldSquareBgUrl) {
      const safeUrl = soldSquareBgUrl.replace(/'/g, '%27');
      return {
        backgroundImage: `linear-gradient(180deg,rgba(0,0,0,0.42) 0%,rgba(0,0,0,0.55) 100%),url('${safeUrl}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        ...(planColor ? { boxShadow: `inset 0 0 0 3px ${planColor}` } : {}),
      };
    }
    if (planColor) {
      return { backgroundColor: planColor, opacity: reserved ? 0.7 : 1 };
    }
    return {
      backgroundColor: sold
        ? '#888'
        : reserved
          ? '#f0ad4e'
          : selected
            ? '#222'
            : cat.color || '#bbb',
    };
  };

  const renderCell = (casilla, cat, rIdx, cIdx) => {
    if (!casilla) {
      return (
        <div
          key={`${rIdx}-${cIdx}`}
          className='aspect-square w-full rounded opacity-30'
          style={{ backgroundColor: cat.color || '#bbb' }}
        />
      );
    }
    const sold = casilla.estado === 'Vendido';
    const reserved = casilla.estado === 'Reservado';
    const sel = isSelected(casilla);
    const canClick = !sold && !reserved;

    return (
      <button
        key={casilla._id}
        type='button'
        disabled={sold || reserved}
        onClick={() => toggleCasilla(casilla)}
        title={
          sold
            ? `${casilla.nombreComprador || t('board.sold', 'Vendido')}${
                shared && casilla.categoria ? ` · ${casilla.categoria}` : ''
              }`
            : reserved
              ? t('board.reserved', 'Reservado')
              : shared
                ? t('board.available', 'Disponible')
                : `$${casilla.precioCasilla}`
        }
        className={`aspect-square w-full rounded text-[10px] font-bold text-white transition-all flex items-center justify-center ${
          canClick ? 'cursor-pointer hover:opacity-90' : 'cursor-default'
        } ${sel ? 'ring-2 ring-ink ring-offset-1' : ''}`}
        style={getSquareStyle(casilla, cat)}
      >
        {sold ? (
          <span className='text-[8px] truncate px-1'>
            {casilla.nombreComprador?.trim()?.split(' ')[0] || '✓'}
          </span>
        ) : reserved ? (
          <MdBookmark className='h-3.5 w-3.5' />
        ) : sel ? (
          '✓'
        ) : null}
      </button>
    );
  };

  if (loading) return <BoardTicketsRifaSkeleton />;
  if (error) return <MessageBox variant='danger'>{error}</MessageBox>;

  /* ── Buy button — shared markup, rendered in two places ─────────────────── */
  const buyButton = (extraClass = '') => (
    <button
      type='button'
      onClick={handleProceed}
      disabled={selectedCasillas.length === 0}
      className={`flex w-full items-center justify-between rounded-xl bg-ink px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90 active:opacity-80 disabled:opacity-40 ${extraClass}`}
    >
      <div className='flex items-center gap-2'>
        <ShoppingCart className='h-5 w-5' />
        <span>
          {selectedCasillas.length === 0
            ? 'Comprar bonos'
            : `Comprar bonos (${selectedCasillas.length})`}
        </span>
      </div>
      {totalPrice > 0 && (
        <div className='flex items-center gap-2'>
          <span className='font-bold'>${totalPrice}</span>
          <span className='opacity-70'>→</span>
        </div>
      )}
    </button>
  );

  return (
    <div className='min-h-screen bg-white font-sans text-ink'>
      {/* ─── Top bar ─────────────────────────────────────────── */}
      <div className='flex items-center gap-3 px-4 py-4 lg:px-8 lg:py-5'>
        <button
          onClick={() => navigate(-1)}
          className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surface'
          aria-label='Volver'
        >
          <ArrowLeft className='h-4 w-4' />
        </button>
        <h1 className='truncate text-base font-semibold lg:text-lg'>{rifaDetails?.nombre}</h1>
      </div>

      {/* ─── Main content ────────────────────────────────────── */}
      {/* Mobile: single column + bottom padding for fixed bar   */}
      {/* Desktop: two-column grid, no fixed bar needed          */}
      <div className='mx-auto max-w-7xl px-4 pb-28 lg:px-8 lg:pb-12'>
        <div className='lg:grid lg:grid-cols-5 lg:items-start lg:gap-10'>
          {/* ── Left column: rifa info + desktop buy button ────── */}
          <div className='lg:col-span-2 lg:sticky lg:top-24'>
            <div className='overflow-hidden rounded-2xl bg-surface'>
              {rifaDetails?.rifaImage?.fileLink && (
                <img
                  src={rifaDetails.rifaImage.fileLink}
                  alt={rifaDetails.nombre}
                  className='w-full aspect-video object-cover'
                />
              )}
              <div className='p-4'>
                <h2 className='text-xl font-bold leading-snug'>{rifaDetails?.nombre}</h2>
                {rifaDetails?.descripcion && (
                  <p className='mt-1 text-sm text-zinc-500'>{rifaDetails.descripcion}</p>
                )}
              </div>
            </div>

            {/* Buy button — desktop only, part of normal flow */}
            <div className='mt-4 hidden lg:block'>{buyButton()}</div>
          </div>

          {/* ── Right column: category list + boards ───────────── */}
          <div className='lg:col-span-3 mt-5 lg:mt-0 space-y-5'>
            {/* Options list (one board per category) or single shared board */}
            {shared ? (
              <div>
                <p className='mb-3 text-base font-semibold'>
                  {t('board.selectSquares', 'Elegí tus casillas')}
                </p>
                <div
                  className='grid w-full gap-1'
                  style={{ gridTemplateColumns: `repeat(${sharedGrid.columnas}, minmax(0, 1fr))` }}
                >
                  {Array.from({ length: sharedGrid.filas }, (_, r) =>
                    Array.from({ length: sharedGrid.columnas }, (_, col) =>
                      renderCell(
                        casillas.find(c => Number(c.fila) === r && Number(c.columna) === col) ||
                          null,
                        { color: SHARED_FREE_COLOR },
                        r,
                        col
                      )
                    )
                  )}
                </div>
                {/* Plans legend: price and numbers are chosen per square in the next step */}
                <div className='mt-4 space-y-1.5'>
                  <p className='text-sm font-semibold'>{t('board.plans', 'Planes')}</p>
                  {(rifaDetails?.config?.categories || []).map(plan => (
                    <div key={plan.nombre} className='flex items-center gap-2 text-sm'>
                      <span
                        className='h-3 w-3 shrink-0 rounded-full'
                        style={{ backgroundColor: plan.color || '#888' }}
                      />
                      <span className='flex-1 font-medium'>{plan.nombre}</span>
                      {toTicketsPerSquare(plan.ticketsPorCasilla) > 0 && (
                        <span className='text-xs text-zinc-400'>
                          {t('board.numbersIncluded', {
                            count: toTicketsPerSquare(plan.ticketsPorCasilla),
                            defaultValue: '{{count}} números incluidos',
                          })}
                        </span>
                      )}
                      <span className='font-semibold'>${plan.precioCasilla}</span>
                    </div>
                  ))}
                  <p className='pt-1 text-xs text-zinc-500'>
                    {t(
                      'board.planChosenNext',
                      'Elegís el plan de cada casilla en el próximo paso.'
                    )}
                  </p>
                </div>
                <div className='mt-3 flex gap-4 text-xs text-zinc-500'>
                  <span className='flex items-center gap-1'>
                    <MdBookmark className='h-3 w-3' /> {t('board.reserved', 'Reservado')}
                  </span>
                  <span className='flex items-center gap-1'>
                    <span className='h-2.5 w-2.5 rounded bg-ink ring-1 ring-ink ring-offset-1' />{' '}
                    {t('board.selected', 'Seleccionado')}
                  </span>
                </div>
              </div>
            ) : (
              <div>
                <p className='mb-3 text-base font-semibold'>Opciones:</p>
                <div className='space-y-2'>
                  {categories.map(cat => {
                    const isOpen = expandedCategory === cat.nombre;
                    const catCasillas = casillasByCategory[cat.nombre] || [];
                    const available = catCasillas.filter(c => c.estado === 'Disponible').length;
                    const selectedInCat = selectedCasillas.filter(
                      c => String(c.categoria).trim() === cat.nombre
                    ).length;

                    return (
                      <div
                        key={cat.nombre}
                        className='overflow-hidden rounded-xl border border-divider bg-surface'
                      >
                        {/* Category header row */}
                        <button
                          type='button'
                          onClick={() => setExpandedCategory(isOpen ? null : cat.nombre)}
                          className='flex w-full items-center gap-3 px-4 py-3 text-left'
                        >
                          {/* Color dot */}
                          <span
                            className='h-3 w-3 shrink-0 rounded-full'
                            style={{ backgroundColor: cat.color || '#888' }}
                          />
                          {/* Name */}
                          <span
                            className='flex-1 text-sm font-bold italic'
                            style={{ color: cat.color || '#000' }}
                          >
                            {cat.nombre}
                            {selectedInCat > 0 && (
                              <span className='ml-2 rounded-full bg-ink px-1.5 py-0.5 text-[10px] font-semibold not-italic text-white'>
                                {selectedInCat}
                              </span>
                            )}
                          </span>
                          {/* Price */}
                          <span className='text-sm font-semibold'>${cat.precioCasilla}</span>
                          {/* Available */}
                          <span className='text-xs text-zinc-400'>{available} disp.</span>
                          {/* Chevron */}
                          <ChevronDown
                            className={`h-4 w-4 shrink-0 text-zinc-400 transition-transform duration-200 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          />
                        </button>

                        {/* Expanded board grid */}
                        {isOpen && (
                          <div className='border-t border-divider px-4 py-4'>
                            {/* Price info */}
                            <p className='mb-3 text-xs text-zinc-500'>
                              ${cat.precioCasilla} {t('board.perSquare', 'por casilla')}
                              {toTicketsPerSquare(cat.ticketsPorCasilla) > 0 &&
                                ` · ${t('board.numbersIncluded', {
                                  count: toTicketsPerSquare(cat.ticketsPorCasilla),
                                  defaultValue: '{{count}} números incluidos',
                                })}`}
                            </p>

                            {/* Grid */}
                            <div
                              className='grid w-full gap-1'
                              style={{
                                gridTemplateColumns: `repeat(${cat.columnas}, minmax(0, 1fr))`,
                              }}
                            >
                              {buildGrid(cat.nombre, cat.filas, cat.columnas).flatMap((row, rIdx) =>
                                row.map((casilla, cIdx) => renderCell(casilla, cat, rIdx, cIdx))
                              )}
                            </div>

                            {/* Legend */}
                            <div className='mt-3 flex gap-4 text-xs text-zinc-500'>
                              <span className='flex items-center gap-1'>
                                <span className='h-2.5 w-2.5 rounded bg-[#888]' /> Vendido
                              </span>
                              <span className='flex items-center gap-1'>
                                <span className='h-2.5 w-2.5 rounded bg-[#f0ad4e]' /> Reservado
                              </span>
                              <span className='flex items-center gap-1'>
                                <span className='h-2.5 w-2.5 rounded bg-ink ring-1 ring-ink ring-offset-1' />{' '}
                                Seleccionado
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ─── Mobile-only sticky buy bar (hidden on lg+) ──────── */}
      <div className='fixed bottom-0 left-0 right-0 z-30 border-t border-divider bg-white px-4 py-3 lg:hidden'>
        {buyButton()}
      </div>
    </div>
  );
}
